import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiGift,
  FiCalendar,
  FiAward,
  FiTrendingUp,
  FiCheckCircle,
  FiX
} from 'react-icons/fi';
import { sendApiRequest } from '../utils';

const Rewards = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const response = await sendApiRequest('api/rewards', {}, 'GET');
        const activeRewards = (Array.isArray(response.data) ? response.data : []).filter(
          r => r.status === 'active'
        ).sort((a, b) => a.requiredBookings - b.requiredBookings);
        setRewards(activeRewards);
      } catch (error) {
        console.error('Failed to fetch rewards:', error);
        setRewards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRewards();
  }, []);

  const getRewardTypeIcon = (type) => {
    switch (type) {
      case 'trip':
        return '✈️';
      case 'vehicle':
        return '🚗';
      case 'cash':
        return '💰';
      default:
        return '🎁';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ongoing';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto'></div>
          <p className='mt-4 text-gray-500 font-medium'>Loading rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-12 px-4'>
      <div className='max-w-6xl mx-auto'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className='text-center mb-12'
        >
          <span className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 text-sm font-semibold mb-4'>
            <FiAward className='w-4 h-4' />
            Exciting Offers
          </span>
          <h1 className='text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4'>
            Summer Bumper Schemes
          </h1>
          <p className='text-gray-600 max-w-2xl mx-auto text-lg'>
            Book more, earn more! Check out our amazing reward schemes for partners
          </p>
        </motion.div>

        {rewards.length === 0 ? (
          <div className='text-center py-16'>
            <FiX className='w-16 h-16 text-gray-300 mx-auto mb-4' />
            <h3 className='text-xl font-semibold text-gray-600 mb-2'>No rewards available right now</h3>
            <p className='text-gray-500'>Check back soon for exciting offers!</p>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {rewards.map((reward, index) => (
              <motion.div
                key={reward._id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className='bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300'
              >
                <div className='relative'>
                  {reward.image && (
                    <img
                      src={reward.image}
                      alt={reward.title}
                      className='w-full h-48 object-cover'
                    />
                  )}
                  {!reward.image && (
                    <div className='w-full h-48 bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center'>
                      <span className='text-7xl'>{getRewardTypeIcon(reward.rewardType)}</span>
                    </div>
                  )}
                  <div className='absolute -bottom-4 -right-4 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-6 py-2 rounded-full shadow-lg font-bold text-2xl'>
                    {reward.requiredBookings}
                  </div>
                </div>

                <div className='p-6'>
                  <div className='mb-3'>
                    <span className='inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 mb-3 capitalize'>
                      {reward.rewardType}
                    </span>
                    <h3 className='text-xl font-bold text-gray-800 mb-2'>{reward.title}</h3>
                    {reward.description && (
                      <p className='text-gray-600 text-sm mb-4'>{reward.description}</p>
                    )}
                  </div>

                  <div className='space-y-2 mb-6'>
                    <div className='flex items-center gap-2 text-gray-600 text-sm'>
                      <FiCalendar className='w-4 h-4' />
                      <span>
                        {formatDate(reward.startDate)} - {formatDate(reward.endDate)}
                      </span>
                    </div>
                    {reward.rewardValue > 0 && (
                      <div className='flex items-center gap-2 text-gray-600 text-sm'>
                        <FiTrendingUp className='w-4 h-4' />
                        <span>
                          ₹{reward.rewardValue.toLocaleString('en-IN')} value
                        </span>
                      </div>
                    )}
                  </div>

                  <div className='pt-4 border-t border-gray-100'>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-2 text-emerald-600 font-semibold'>
                        <FiCheckCircle className='w-5 h-5' />
                        <span>Book {reward.requiredBookings} plots</span>
                      </div>
                      <div className='text-2xl'>{getRewardTypeIcon(reward.rewardType)}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className='mt-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl'
        >
          <div className='flex flex-col md:flex-row items-center gap-6'>
            <div className='flex-1 text-center md:text-left'>
              <h2 className='text-2xl font-bold mb-3'>
                Book More, Earn More, Enjoy More!
              </h2>
              <p className='text-indigo-100'>
                Unlock amazing rewards by achieving booking milestones. The more you book, the bigger the rewards!
              </p>
            </div>
            <div className='flex-shrink-0 text-6xl'>
              🎉
            </div>
          </div>
        </motion.div>

        <div className='mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-6'>
          <h3 className='text-xl font-bold text-gray-800 mb-4 flex items-center gap-2'>
            <FiCheckCircle className='text-amber-500 w-6 h-6' />
            Important Note
          </h3>
          <ul className='space-y-2 text-gray-600'>
            <li className='flex items-start gap-2'>
              <span className='w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold shrink-0'>1</span>
              <span>Each booked package must have at least 5 EMI payments completed to qualify for offers</span>
            </li>
            <li className='flex items-start gap-2'>
              <span className='w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold shrink-0'>2</span>
              <span>Bookings from both you and your entire team count towards your reward requirements!</span>
            </li>
            <li className='flex items-start gap-2'>
              <span className='w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold shrink-0'>3</span>
              <span>Offer plot won't be cancelled</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Rewards;
